import React, { useMemo, useState } from "react";

import {
  calculateFootprintingProgress,
  isPercentageComplete,
  queryIsLoading,
  useGetCarbonProgramByFarmId,
  useGetFootprintingCropFootprints,
  useGetFootprintingFarmFootprint,
  useParams,
  useSubmitFootprintingCropPrimaryData,
  useSubmitFootprintingFarmPrimaryData,
  useTranslation,
  useUserContext,
} from "utils";

import { CropFootprintReturnBody } from "models";

import { Button, Col, Flex, MainPage, Row } from "components";

import { FootprintingCropReview } from "pages/app/footprinting/components/FootprintingCropReview";
import { FootprintingFarmReview } from "pages/app/footprinting/components/FootprintingFarmReview";
import { FootprintingReviewSteps } from "pages/app/footprinting/components/FootprintingReviewSteps";
import { FootprintingSuccessScreen } from "pages/app/footprinting/components/FootprintingSuccessScreen";
import { SubmitForVerificationCard } from "pages/app/footprinting/components/SubmitForVerificationCard";

interface ReviewProps {
  year: number;
}

export const Review = ({ year }: ReviewProps) => {
  const { t } = useTranslation(["footprinting"]);
  const { user } = useUserContext();
  const farmId = user?.farm.id ?? undefined;

  const carbonProgramQuery = useGetCarbonProgramByFarmId(farmId);
  const carbonProgram = carbonProgramQuery.data;
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [allCheckboxesChecked, setAllCheckboxesChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const farmProgressQuery = useGetFootprintingFarmFootprint(farmId, year);
  const cropProgressQueries = useGetFootprintingCropFootprints(farmId, [year], carbonProgram?.farmLevelFootprinting?.programCrops ?? []);

  const isLoading = isSubmitting && [carbonProgramQuery, farmProgressQuery, ...cropProgressQueries].some((query) => queryIsLoading(query));

  const { readyToSubmit, isSubmitted } = useMemo(() => {
    if (isLoading) {
      return calculateFootprintingProgress(undefined, []);
    }

    return calculateFootprintingProgress(
      farmProgressQuery.data,
      cropProgressQueries.filter((cpq) => !!cpq.data).map((cpq) => cpq.data)
    );
  }, [farmProgressQuery.data, cropProgressQueries, isLoading]);

  const cropsToReview = cropProgressQueries
    .filter((cpq) => cpq.data !== undefined && isPercentageComplete(cpq.data.completionPct))
    .map((cpq) => cpq.data as CropFootprintReturnBody);

  const { mutate: mutateSubmitCropPrimaryData } = useSubmitFootprintingCropPrimaryData(
    farmId ?? "",
    year,
    cropsToReview.map((crop) => ({ cropId: crop.cropId, primaryDataId: crop.footprint?.id ?? "" }))
  );

  const { mutate: mutateSubmitFarmPrimaryData } = useSubmitFootprintingFarmPrimaryData(farmId ?? "", year, farmProgressQuery.data?.footprint?.id ?? "");

  const handleCheckboxesChange = (allChecked: boolean) => {
    setAllCheckboxesChecked(allChecked);
  };

  const handleSubmit = () => {
    if (!allCheckboxesChecked || isLoading || !farmId || !readyToSubmit) {
      return;
    }

    setIsSubmitting(true);
    Promise.all([mutateSubmitFarmPrimaryData(), mutateSubmitCropPrimaryData()]).finally(() => setIsSubmitting(false));
  };

  // Just to get TS to stop complaining about farmId being maybe undefined
  if (!farmId) {
    return null;
  }

  if (carbonProgram !== undefined && isSubmitted) {
    return <FootprintingSuccessScreen carbonProgram={carbonProgram} year={year} farmId={farmId} />;
  }

  return (
    <Row justify="space-between">
      <Col sm={10} xs={8}>
        <FootprintingReviewSteps farmId={farmId} cropIds={cropsToReview.map((cfd) => cfd.cropId)} currentStep={currentStep} year={year} isLoading={isLoading} />
      </Col>

      <Col sm={13} xs={10}>
        {currentStep === 0 && <FootprintingFarmReview year={year} />}
        {currentStep > 0 && currentStep <= cropsToReview.length && <FootprintingCropReview cropId={cropsToReview[currentStep - 1].cropId} year={year} />}
        {currentStep === cropsToReview.length + 1 && (
          <SubmitForVerificationCard
            farmId={farmId}
            carbonProgram={carbonProgram}
            year={year}
            onCheckboxChange={handleCheckboxesChange}
            formSubmitted={isSubmitted}
          />
        )}
        <Flex justify="space-between" className="padding-125 margin-bottom-100">
          {currentStep > 0 && (
            <Button
              dataTestId="footprinting-review-back-button"
              className="review-navigation-button"
              type="tertiary"
              onClick={() => setCurrentStep((prev) => Math.max(prev - 1, 0))}
              disabled={isLoading}
            >
              {t("review.back")}
            </Button>
          )}
          {currentStep <= cropsToReview.length && (
            <Button
              dataTestId="footprinting-review-next-button"
              className="review-navigation-button"
              type="primary"
              onClick={() => setCurrentStep((prev) => Math.min(prev + 1, cropsToReview.length + 1))}
              disabled={isLoading}
            >
              {t("review.continue")}
            </Button>
          )}
          {currentStep === cropsToReview.length + 1 && (
            <Button
              dataTestId="footprinting-review-submit-button"
              className="review-navigation-button"
              type="primary"
              onClick={handleSubmit}
              disabled={!readyToSubmit && !allCheckboxesChecked && isLoading}
            >
              {t("review.submit", { year })}
            </Button>
          )}
        </Flex>
      </Col>
    </Row>
  );
};

export const Wrapper = (p: React.PropsWithChildren) => {
  const { t } = useTranslation([]);
  const params = useParams();
  const { user } = useUserContext();

  const year = Number.parseInt(params.year ?? "");
  if (!year) {
    return null;
  }

  return (
    <MainPage
      pageName="Review & Submit"
      navigationCrumbs={{
        crumbs: [
          { name: "Home", link: "/dashboard" },
          { name: user?.farm?.name || t("modules:navigation.overview.farm") },
          { name: t("footprinting:footprintingLandingPage.breadcrumb"), link: "footprinting" },
          { name: "Review & Submit" },
        ],
      }}
    >
      <Review {...p} year={year} />
    </MainPage>
  );
};

export default Wrapper;
